import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  resolveAdSize,
  AD_SIZE_PRESETS,
  AD_MOBILE_MAX_WIDTH,
  type AdSizeSpec,
} from "@shared/adSlots";
import { adCreativeWidth, sizedUploadUrl } from "../../lib/imageUrl";

interface AdSpaceProps {
  className?: string;
  label?: string;
  size?: "leaderboard" | "rectangle" | "skyscraper" | "native-tip";
  slotKey?: string;
  /**
   * Extra slot keys to try, in order, when `slotKey` holds no active creative.
   * Lets a placement prefer its own slot (e.g. `home_rectangle`) and still fall
   * back to the shared pool (`rectangle`).
   */
  fallbackSlotKeys?: (string | null | undefined)[];
}

type PublicAd = {
  id: number;
  slotKey: string;
  title?: string | null;
  type: "image" | "html" | "script";
  imageUrl?: string | null;
  linkUrl?: string | null;
  html?: string | null;
  script?: string | null;
  /** Optional small-screen creative; falls back to the fields above when empty. */
  mobileType?: "image" | "html" | "script" | null;
  mobileImageUrl?: string | null;
  mobileLinkUrl?: string | null;
  mobileHtml?: string | null;
  mobileScript?: string | null;
  /** Declared slot size, e.g. "728x90" — used as the sizing hint. */
  size?: string | null;
};

type SlotMeta = {
  enabled?: boolean;
  /** Big-screen size guide. */
  size?: string | null;
  /** Small-screen size guide, used on screens ≤ AD_MOBILE_MAX_WIDTH. */
  mobileSize?: string | null;
};

/** The creative actually rendered once the viewport has been taken into account. */
type AdVariant = {
  type: "image" | "html" | "script";
  imageUrl: string | null;
  linkUrl: string | null;
  html: string | null;
  script: string | null;
  isMobile: boolean;
};

/** True when the ad carries a usable small-screen creative. */
function pickMobileVariant(ad: PublicAd): AdVariant | null {
  const type = ad.mobileType || null;
  const usable =
    (type === "image" && !!ad.mobileImageUrl) ||
    (type === "html" && !!ad.mobileHtml) ||
    (type === "script" && !!ad.mobileScript);
  if (!usable || !type) return null;

  return {
    type,
    imageUrl: ad.mobileImageUrl || null,
    linkUrl: ad.mobileLinkUrl || ad.linkUrl || null,
    html: ad.mobileHtml || null,
    script: ad.mobileScript || null,
    isMobile: true,
  };
}

/** Viewport watcher — the creative swaps live on resize/rotate, no reload needed. */
function useIsSmallScreen() {
  const query = `(max-width: ${AD_MOBILE_MAX_WIDTH}px)`;
  const [isSmall, setIsSmall] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setIsSmall(event.matches);
    setIsSmall(mql.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    // Safari < 14
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return isSmall;
}

/**
 * Sizing rule — the creative box is built from the creative's own aspect ratio so
 * it fits it exactly:
 *
 *  - the box is as wide as the slot, or as wide as `heightCap × ratio` when that
 *    is narrower → wide banners span the whole slot, tall/square units stay sane
 *  - the box height is that width ÷ ratio → it hugs the creative, so there is no
 *    empty band above/below/beside it
 *
 * Nothing is cropped, stretched, boxed in, or padded.
 *
 * Occupancy rule — **`AdSpace` renders nothing at all when it has no creative to
 * show**. A slot switched off in Admin → Ads → Slots (or simply left empty) leaves
 * no wrapper, no placeholder and no reserved gap on the page.
 */
const AD_MIN_MAX_HEIGHT = 400;
/** Taller ceiling on phones/small tablets, where a full-width unit is normal. */
const AD_MIN_MAX_HEIGHT_MOBILE = 600;

function buildSrcDoc(ad: AdVariant): string {
  const html = ad.type === "html" ? (ad.html || "") : "";
  const script = ad.type === "script" ? (ad.script || "") : "";
  const body = html || "<div id=\"ad-root\"></div>";
  const js = script ? `\n<script>\n${script}\n</script>` : "";

  return `<!doctype html><html><head><meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>html,body{margin:0;padding:0;background:transparent}*{box-sizing:border-box}</style>
</head><body>${body}${js}</body></html>`;
}

/**
 * Renders an image creative in a box that matches the file's own aspect ratio.
 * The ratio is read from the decoded image, so once it loads the box == the
 * creative exactly (no letterbox bands, no cropping); before that it falls back
 * to the slot's declared ratio.
 *
 * CLS NOTE — read this before changing the sizing, and before blaming this
 * component for a layout shift.
 *
 * The box is `max-width: heightCap × ratio` with `aspect-ratio: ratio`. When the
 * available width is the binding constraint (which it is in the sidebar), the
 * box height becomes `availableWidth ÷ ratio`. So if the *measured* ratio
 * differs from the *fallback* ratio, the box is one height before the image
 * loads and another after — a one-shot vertical jump that pushes everything
 * below it.
 *
 * The fallback ratio is `spec.w / spec.h`, i.e. the size declared on the ad slot
 * in Admin → Ads (see `resolveAdSize` in shared/adSlots.ts). The fix is therefore
 * data, not code: **declare each creative's slot size as its real pixel size**
 * (or export the creative at the declared size). When the two agree the ratio is
 * stable from the first frame and nothing moves; when they disagree — e.g. a
 * 2048×313 banner in a slot declared 300×250 — the box is ~5x too tall until the
 * bytes land.
 *
 * Recording the uploaded file's intrinsic dimensions on the ad record and using
 * those as the fallback would remove the dependency on that convention
 * entirely. That needs a schema change, so it is not done here.
 */
export function AdImageBox({
  src,
  alt,
  heightCap,
  fallbackRatio,
  targetWidth,
  loading = "lazy",
}: {
  src: string;
  alt: string;
  heightCap: number;
  fallbackRatio: number;
  /**
   * CSS width the creative is drawn at. Omit it and the full-size original is
   * served, exactly as before this existed — that is the behaviour the admin
   * preview in AdminAds.tsx wants.
   */
  targetWidth?: number;
  loading?: "lazy" | "eager";
}) {
  const [ratio, setRatio] = useState<number | null>(null);
  const effectiveRatio = ratio && Number.isFinite(ratio) && ratio > 0 ? ratio : fallbackRatio;

  return (
    <div
      className="mx-auto w-full"
      style={{ maxWidth: Math.max(1, Math.round(heightCap * effectiveRatio)) }}
    >
      <div className="relative w-full" style={{ aspectRatio: `${effectiveRatio}` }}>
        <img
          src={sizedUploadUrl(src, targetWidth ?? 0)}
          alt={alt}
          loading={loading}
          decoding="async"
          className="absolute inset-0 h-full w-full object-contain"
          onLoad={(e) => {
            const el = e.currentTarget;
            if (el.naturalWidth > 0 && el.naturalHeight > 0) {
              setRatio(el.naturalWidth / el.naturalHeight);
            }
          }}
        />
      </div>
    </div>
  );
}

export function AdSpace({
  className = "",
  label = "Advertisement",
  size = "rectangle",
  slotKey,
  fallbackSlotKeys,
}: AdSpaceProps) {
  // Priority order: explicit slot key → placement fallbacks → the size preset key.
  const candidateKeys = useMemo(() => {
    const list: string[] = [];
    const push = (value?: string | null) => {
      const key = String(value ?? "").trim();
      if (key && !list.includes(key)) list.push(key);
    };
    push(slotKey);
    (fallbackSlotKeys || []).forEach(push);
    push(size);
    return list;
  }, [slotKey, fallbackSlotKeys, size]);

  const seed = useMemo(() => Math.random().toString(36).slice(2), []);
  const requestKeys = candidateKeys.join(",");

  const { data } = useQuery<Record<string, unknown>>({
    queryKey: ["/api/ads", requestKeys, seed],
    queryFn: async () => {
      const res = await fetch(`/api/ads?slots=${encodeURIComponent(requestKeys)}`, {
        cache: "no-store",
      });
      if (!res.ok) return {};
      const json = await res.json();
      return json && typeof json === "object" ? (json as Record<string, unknown>) : {};
    },
    staleTime: 0,
    gcTime: 0,
    retry: false,
    enabled: candidateKeys.length > 0,
  });

  const { ad, activeKey, slotMeta } = useMemo(() => {
    const metaFor = (key: string): SlotMeta | null => {
      const raw = data?.["_slots"];
      if (!raw || typeof raw !== "object") return null;
      const entry = (raw as Record<string, unknown>)[key];
      return entry && typeof entry === "object" ? (entry as SlotMeta) : null;
    };

    for (const key of candidateKeys) {
      const meta = metaFor(key);
      // A slot switched off in the admin kills the placement outright — we do not
      // fall through to the shared pool, so "off" really means nothing here.
      if (meta?.enabled === false) {
        return { ad: null as PublicAd | null, activeKey: key, slotMeta: meta };
      }
      const found = data?.[key];
      if (found && typeof found === "object") {
        return { ad: found as PublicAd, activeKey: key, slotMeta: meta };
      }
    }
    return {
      ad: null as PublicAd | null,
      activeKey: candidateKeys[0] || size,
      slotMeta: metaFor(candidateKeys[0] || size),
    };
  }, [data, candidateKeys, size]);

  const isSmallScreen = useIsSmallScreen();

  /**
   * Size guide in force. On phones/small tablets the slot's small-screen guide wins,
   * so both the height ceiling and any HTML/JS box match the size the admin was
   * shown when uploading the mobile creative.
   */
  const spec: AdSizeSpec = useMemo(() => {
    const guides = isSmallScreen
      ? [slotMeta?.mobileSize, ad?.size, activeKey]
      : [slotMeta?.size, ad?.size, activeKey];

    for (const guide of guides) {
      const resolved = resolveAdSize(guide);
      if (resolved) return resolved;
    }
    return AD_SIZE_PRESETS[size] || AD_SIZE_PRESETS.rectangle;
  }, [isSmallScreen, slotMeta?.mobileSize, slotMeta?.size, ad?.size, activeKey, size]);

  // Small screens use the ad's own small-screen creative when it has one; otherwise
  // the desktop creative is shown (and squeezed to fit) as before.
  const variant = useMemo<AdVariant | null>(() => {
    if (!ad) return null;
    if (isSmallScreen) {
      const mobile = pickMobileVariant(ad);
      if (mobile) return mobile;
    }
    return {
      type: ad.type,
      imageUrl: ad.imageUrl || null,
      linkUrl: ad.linkUrl || null,
      html: ad.html || null,
      script: ad.script || null,
      isMobile: false,
    };
  }, [ad, isSmallScreen]);

  // Every hook above runs unconditionally; from here we may render nothing at all.
  if (!ad || !variant) return null;

  // Small screens get a taller ceiling so a phone-shaped creative is not squeezed.
  const heightCap = Math.max(spec.h, isSmallScreen ? AD_MIN_MAX_HEIGHT_MOBILE : AD_MIN_MAX_HEIGHT);
  const fallbackRatio = spec.w / spec.h;
  /*
   * Sized against the slot's DECLARED width, not the box's computed max-width:
   * the measured ratio only arrives after the image loads, so a hint derived
   * from it would change mid-flight and trigger a second request. 2x covers a
   * 2x screen. See `client/src/lib/imageUrl.ts`.
   */
  const creativeWidth = adCreativeWidth(spec.w);
  const variantKey = `${variant.type}:${variant.imageUrl || ""}:${variant.html ? "h" : ""}${
    variant.script ? "s" : ""
  }`;
  const wrapperProps = {
    className: `w-full flex items-center justify-center relative overflow-hidden ${className}`,
    "data-ad-slot": activeKey,
    "data-ad-size": spec.label,
    "data-ad-variant": variant.isMobile ? "mobile" : "desktop",
  } as const;

  if (variant.type === "image" && variant.imageUrl) {
    const box = (
      <AdImageBox
        key={variantKey}
        src={variant.imageUrl}
        alt={ad.title || label}
        heightCap={heightCap}
        fallbackRatio={fallbackRatio}
        targetWidth={creativeWidth}
      />
    );

    return (
      <div {...wrapperProps}>
        {variant.linkUrl ? (
          <a href={variant.linkUrl} target="_blank" rel="noreferrer" className="block w-full">
            {box}
          </a>
        ) : (
          box
        )}
      </div>
    );
  }

  if (variant.type === "html" || variant.type === "script") {
    // Third-party creatives have no intrinsic ratio, so the slot's declared size
    // sets the box, capped in height the same way.
    const srcDoc = buildSrcDoc(variant);
    return (
      <div {...wrapperProps}>
        <div className="mx-auto w-full" style={{ maxWidth: Math.round(heightCap * fallbackRatio) }}>
          <div className="relative w-full" style={{ aspectRatio: `${spec.w} / ${spec.h}` }}>
            <iframe
              key={variantKey}
              title={ad.title || label}
              className="absolute inset-0 h-full w-full"
              sandbox="allow-scripts allow-forms allow-popups allow-top-navigation-by-user-activation"
              referrerPolicy="no-referrer"
              srcDoc={srcDoc}
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
}
